<script setup>
import { onMounted, onUnmounted, ref, defineProps, watch, computed } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '@/composables/useTheme';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const props = defineProps({
  locations: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: "District 1 - Central Business",
        address: "123 Nguyen Hue Street, District 1",
        coordinates: [10.7769, 106.7009],
        status: "online",
        lockers: 24,
        occupancy: 85
      },
      {
        id: 2,
        name: "District 3 - Residential Area",
        address: "456 Vo Van Tan Street, District 3",
        coordinates: [10.7829, 106.6889],
        status: "online",
        lockers: 18,
        occupancy: 72
      },
      {
        id: 3,
        name: "District 7 - New Urban Area",
        address: "789 Nguyen Thi Thap Street, District 7",
        coordinates: [10.7329, 106.7229],
        status: "maintenance",
        lockers: 32,
        occupancy: 45
      },
      {
        id: 4,
        name: "Binh Thanh District",
        address: "321 Dien Bien Phu Street, Binh Thanh",
        coordinates: [10.8029, 106.7129],
        status: "online",
        lockers: 16,
        occupancy: 91
      },
      {
        id: 5,
        name: "Tan Binh District - Airport Area",
        address: "654 Truong Chinh Street, Tan Binh",
        coordinates: [10.7929, 106.6529],
        status: "offline",
        lockers: 28,
        occupancy: 0
      }
    ]
  }
});

const mapContainer = ref(null);
let map = null;
let markers = [];
let tileLayer = null;

const filter = ref('all');

const { currentTheme } = useTheme();
const isDarkTheme = computed(() => currentTheme.value === 'dark');

const STATUS_META = {
  online: { color: '#4CAF50', icon: 'access-point', label: 'Online' },
  offline: { color: '#8D96B2', icon: 'web-off', label: 'Offline' },
  maintenance: { color: '#FB8C00', icon: 'wrench-outline', label: 'Maintenance' },
  error: { color: '#E21D26', icon: 'alert-outline', label: 'Error' },
};

const legendItems = computed(() =>
  Object.entries(STATUS_META)
    .map(([status, meta]) => ({
      status,
      ...meta,
      count: props.locations.filter((l) => l.status === status).length,
    }))
    .filter((item) => item.count > 0)
);

const createCustomIcon = (status) => {
  const meta = STATUS_META[status] || STATUS_META.online;
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 26px;
      height: 26px;
      background: ${meta.color};
      border: 2px solid white;
      border-radius: 999px;
      box-shadow: 0 3px 8px rgba(15,27,64,0.28);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    "><span class="mdi mdi-${meta.icon}" style="font-size:14px;line-height:1;"></span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });
};

function updateMarkers() {
  markers.forEach(m => m.remove());
  markers = [];
  props.locations.filter(loc => {
    if (filter.value === 'all') return true;
    return loc.status === filter.value;
  }).forEach(location => {
    const marker = L.marker(location.coordinates, {
      icon: createCustomIcon(location.status)
    }).addTo(map);

    const isDark = isDarkTheme.value;
    const textColor = isDark ? 'rgba(255,255,255,0.92)' : '#0F1B40';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.62)' : '#6A6A6A';
    const meta = STATUS_META[location.status] || STATUS_META.online;

    const popupContent = `
      <div style="min-width: 200px; font-family: Montserrat, sans-serif;">
        <h4 style="margin: 0 0 6px 0; color: ${textColor}; font-size: 14px; font-weight: 700;">${location.name}</h4>
        <p style="margin: 0 0 8px 0; color: ${secondaryColor}; font-size: 12px; font-weight: 500;">${location.address}</p>
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="color: ${secondaryColor}; font-size: 12px; font-weight: 600;">Lockers: ${location.lockers}</span>
          <span style="color: ${secondaryColor}; font-size: 12px; font-weight: 600;">Occupancy: ${location.occupancy}%</span>
        </div>
        <span style="
          display: inline-flex; align-items: center; gap: 6px;
          height: 24px; padding: 0 10px; border-radius: 999px;
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
          color: ${meta.color}; background: ${meta.color}1f;
        ">${meta.label}</span>
      </div>
    `;
    marker.bindPopup(popupContent, { closeButton: false });
    markers.push(marker);
  });
}

function makeTileLayer() {
  if (isDarkTheme.value) {
    return L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap contributors, © CARTO',
      subdomains: 'abcd',
      maxZoom: 19
    });
  }
  return L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: '© OpenStreetMap contributors, © CARTO',
    subdomains: 'abcd',
    maxZoom: 19
  });
}

function updateMapTheme() {
  if (!map || !tileLayer) return;
  map.removeLayer(tileLayer);
  tileLayer = makeTileLayer().addTo(map);
}

function setFilter(status) {
  filter.value = filter.value === status ? 'all' : status;
}

onMounted(() => {
  if (mapContainer.value) {
    map = L.map(mapContainer.value, { zoomControl: false }).setView([10.7769, 106.7009], 12);
    L.control.zoom({ position: 'topright' }).addTo(map);
    tileLayer = makeTileLayer().addTo(map);
    updateMarkers();
  }
});

onUnmounted(() => {
  if (map) {
    map.remove();
    map = null;
  }
});

watch(filter, () => {
  if (map) updateMarkers();
});

watch(isDarkTheme, () => {
  if (map) {
    updateMapTheme();
    updateMarkers();
  }
});
</script>

<template>
  <div class="lockermap">
    <div ref="mapContainer" class="lockermap__map"></div>
    <div class="map__legend">
      <button
        v-for="item in legendItems"
        :key="item.status"
        class="lg lockermap__lg"
        :class="{ 'is-active': filter === item.status }"
        type="button"
        @click="setFilter(item.status)">
        <span class="mdi" :class="`mdi-${item.icon}`" :style="{ color: item.color }"></span>
        {{ item.label }}
        <span class="n">{{ item.count }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.lockermap {
  position: absolute;
  inset: 0;
}
.lockermap__map {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  overflow: hidden;
}
.lockermap__lg {
  border: 0;
  background: transparent;
  padding: 2px 0;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  min-width: 150px;
}
.lockermap__lg.is-active {
  color: var(--lb-blue);
}
</style>
